/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { ChevronRight, Home } from 'lucide-react'
import { useLocale } from 'next-intl'

import { Link } from '@/i18n/navigation'

/** A single breadcrumb item. */
type BreadcrumbItem = {
  /** The label for the breadcrumb item. */
  label: string

  /** The path for the breadcrumb item. */
  path: string

  /** Whether the breadcrumb item is the current page. */
  current?: boolean
}

/** A description of the available properties for this component. */
type BreadcrumbsProps = {
  /** Array of breadcrumb items to display */
  items: BreadcrumbItem[]

  /** The text to use for the home link (default: 'Home') */
  homeText?: string
}

/**
 * Generic breadcrumbs component that displays a navigation trail based on provided items.
 */
const Breadcrumbs = ({ items, homeText = 'Home' }: BreadcrumbsProps) => {
  const locale = useLocale()
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL

  /**
   * Get the absolute URL for a path. Prepends the site URL to the given path if it is not present (including locale).
   *
   * @param path - path to get the absolute URL for
   */
  const getAbsoluteUrl = (path: string) => {
    if (path.startsWith('http')) return path
    const localePrefix = locale === 'en' ? '' : `/${locale}`
    return `${siteUrl}${localePrefix}${path.startsWith('/') ? path : '/' + path}`
  }

  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: homeText,
      item: getAbsoluteUrl('/'),
    },
    ...items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 2,
      name: item.label,
      item: getAbsoluteUrl(item.path),
    })),
  ]

  // Build the script schema to insert
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: itemListElement,
  }

  return (
    <nav aria-label={'Breadcrumb'} className={'flex'}>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        key='breadcrumb-schema'
      />
      <ol role={'list'} className={'flex items-center space-x-4'}>
        <li>
          <div>
            <Link href={'/'} className={'text-gray-400 hover:text-gray-500'}>
              <Home className={'h-5 w-5 shrink-0'} aria-hidden={'true'} />
              <span className={'sr-only'}>{homeText}</span>
            </Link>
          </div>
        </li>
        {items.map((item) => (
          <li key={item.path}>
            <div className={'flex items-center'}>
              <ChevronRight className={'h-5 w-5 shrink-0 text-gray-400'} aria-hidden={'true'} />
              <Link
                href={item.path}
                aria-current={item.current ? 'page' : undefined}
                className={'ml-4 text-sm font-medium text-gray-500 hover:text-gray-700'}
              >
                {item.label}
              </Link>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  )
}

export { Breadcrumbs }
export type { BreadcrumbItem }
